import s from "./ToolSections.module.scss";

import { ToolDynamicZoneContentItem } from "@/types/strapi/tool";
import { StrapiSectionStepsToolComponentData } from "@/types/strapi/components/tool/sectionSteps";
import { StrapiSectionBenefitsToolComponentData } from "@/types/strapi/components/tool/sectionBenefits";
import { StrapiSectionWhoCanUseToolComponentData } from "@/types/strapi/components/tool/sectionWhoCanUse";
import { StrapiSectionFAQToolComponentData } from "@/types/strapi/components/tool/sectionFAQ";
import { StrapiSectionSEOToolComponentData } from "@/types/strapi/components/tool/sectionSEO";
import { StrapiSectionCTAToolComponentData } from "@/types/strapi/components/tool/sectionCTA";
import { StrapiSectionHorizontalCardsToolComponentData } from "@/types/strapi/components/tool/sectionHorizontalCards";
import { getStrapiAttributes } from "@/helpers/api";
import { StrapiSectionOtherToolsComponentData } from "@/types/strapi/components/tool/sectionOtherTools";
import { StrapiSectionHelpWithEssayToolComponentData } from "@/types/strapi/components/tool/sectionHelpWithEssay";
import { StrapiSectionDefiningWordsToPagesToolComponentData } from "@/types/strapi/components/tool/sectionDefiningWordsToPages";
import dynamic from "next/dynamic";

const SectionToolsWhoCanUse = dynamic(() => import("./SectionToolsWhoCanUse"));

const SectionToolSteps = dynamic(() => import("./SectionToolSteps"));

const SectionToolBenefits = dynamic(() => import("@/components/pages/tools/sections/SectionToolBenefits"));

const SectionSEO = dynamic(() => import("../../../shared/sections/SectionSEO"));

const SectionWordsToPagesDefining = dynamic(
  () => import("../../../tools/words-to-pages/SectionWordsToPagesDefining")
);

const SectionToolsGetHelp = dynamic(() => import("../../../shared/sections/tools/SectionToolsGetHelp"));

const SectionSimpleCTA = dynamic(() => import("../../../shared/sections/SectionSimpleCTA"));

const SectionFaq = dynamic(() => import("../../../shared/sections/SectionFaq"));

const SectionToolsOtherTools = dynamic(() => import("./SectionToolsOtherTools"));

const ToolSectionHorizontalCards = dynamic(() => import("./ToolSectionHorizontalCards"));

type ToolSectionsProps = {
  sections: ToolDynamicZoneContentItem[];
};

export const ToolSections = ({ sections }: ToolSectionsProps) => {
  return (
    <div className={s.wrapper}>
      {sections.map(({ id, __component, ...rest }) => {
        switch (__component) {
          case "tool.section-steps":
            const sectionStepProps = rest as StrapiSectionStepsToolComponentData;
            return (
              <SectionToolSteps
                key={`${__component}-${id}`}
                heading={sectionStepProps.heading}
                subheading={sectionStepProps.subheading}
                items={sectionStepProps.items}
                hint={sectionStepProps.hint}
              />
            );
          case "tool.section-benefits":
            const benefitsProps = rest as StrapiSectionBenefitsToolComponentData;
            return (
              <SectionToolBenefits
                key={`${__component}-${id}`}
                heading={benefitsProps.heading}
                subheading={benefitsProps.subheading}
                items={benefitsProps.items}
                hint={benefitsProps.hint}
              />
            );
          case "tool.section-who-can-use":
            const whoCanUseProps = rest as StrapiSectionWhoCanUseToolComponentData;
            return (
              <SectionToolsWhoCanUse
                key={`${__component}-${id}`}
                heading={whoCanUseProps.heading}
                subheading={whoCanUseProps.subheading}
                items={whoCanUseProps.items}
              />
            );
          case "tool.section-faq":
            const faqProps = rest as StrapiSectionFAQToolComponentData;
            return (
              <SectionFaq
                key={`${__component}-${id}`}
                heading={faqProps.heading}
                subheading={faqProps.subheading}
                faqItems={faqProps.faq}
                editorContent
                microData
              />
            );
          case "tool.section-seo":
            const seoProps = rest as StrapiSectionSEOToolComponentData;
            return <SectionSEO key={`${__component}-${id}`} {...seoProps} />;
          case "tool.section-cta":
            const ctaProps = rest as StrapiSectionCTAToolComponentData;
            return (
              <SectionSimpleCTA
                key={`${__component}-${id}`}
                heading={ctaProps.heading}
                subheading={ctaProps.subheading}
                btnText={ctaProps.buttonText}
                headingType="fullwidth"
                type="cols"
              />
            );
          case "tool.section-horizontal-cards":
            const cardsProps = rest as StrapiSectionHorizontalCardsToolComponentData;
            return (
              <ToolSectionHorizontalCards
                key={`${__component}-${id}`}
                heading={cardsProps.heading}
                subheading={cardsProps.subheading}
                list={cardsProps.list.map(({ image, ...rest }) => {
                  return {
                    ...rest,
                    image: {
                      url: image?.url || "",
                      alt: image?.alternativeText || "Item`s Image",
                    },
                  };
                })}
                gradient={cardsProps.gradient}
              />
            );
          case "tool.section-other-tools":
            const otherToolsProps = rest as StrapiSectionOtherToolsComponentData;
            return (
              <SectionToolsOtherTools
                key={`${__component}-${id}`}
                heading={otherToolsProps.heading}
                subheading={otherToolsProps.subheading}
                tools={
                  otherToolsProps.tools?.map((tool) => ({
                    ...tool,
                    heading: tool.card?.name || tool.heading,
                    url: `/${tool.slug}`,
                  })) || []
                }
                buttonText={otherToolsProps.buttonText}
              />
            );
          case "tool.section-help-with-essay":
            const helpWithEssayProps = rest as StrapiSectionHelpWithEssayToolComponentData;
            return (
              <SectionToolsGetHelp
                key={`${__component}-${id}`}
                heading={helpWithEssayProps.heading}
                subheading={helpWithEssayProps.subheading}
                items={helpWithEssayProps.list.map(({ title, text, icon, buttonText, tag }) => {
                  const tagNumber = (tag === "h3" && 3) || 0;
                  return {
                    heading: title,
                    text,
                    icon,
                    btnText: buttonText,
                    tag: tagNumber,
                  };
                })}
                benefitsTitle={helpWithEssayProps.benefitsTitle}
                benefits={helpWithEssayProps.benefitsList.map(({ emoji, text }) => ({
                  text,
                  emoji: emoji.replace("e-", ""),
                }))}
              />
            );
          case "tool.section-defining-words-to-pages":
            const defSectionProps = rest as StrapiSectionDefiningWordsToPagesToolComponentData;

            return (
              <SectionWordsToPagesDefining
                key={`${__component}-${id}`}
                heading={defSectionProps.heading}
                subheading={defSectionProps.subheading}
                note={
                  "<strong>Please note:</strong> All calculations shown in the table are estimated based on standard requirements."
                }
              />
            );

          default:
            return null;
        }
      })}
    </div>
  );
};
